import styled from "styled-components";
import React from "react";
import {
  ArrowRight,
  CheckCircle2,
  KeyRound,
  Loader2,
  RotateCcw,
} from "lucide-react";
import { Link, router } from "@inertiajs/react";

import { useLanguage } from "../../../../contexts/language";
import { PageHeader } from "../../../../components/page-header";
import { ExitSessionButton } from "../../../../components/exit-session-button";

const getJson = (url) =>
  fetch(url, {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  }).then((res) => res.json().then((json) => ({ ok: res.ok, json })));

const postJson = (url) =>
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "same-origin",
    body: "{}",
  }).then((res) => res.json().then((json) => ({ ok: res.ok, json })));

const rupiah = (value) => `Rp ${Number(value || 0).toLocaleString("id-ID")}`;

const formatDate = (iso, language) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(
    language === "id" ? "id-ID" : "en-GB",
    {
      day: "numeric",
      month: "short",
      year: "numeric",
    },
  );
};

const YourBookings = (props) => {
  const { t, language } = useLanguage();
  const buildingCode = props.building_code;
  const copy = t.yourBookings;
  const list = copy.list;

  const [authed, setAuthed] = React.useState(null);
  const [bookings, setBookings] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [activatingId, setActivatingId] = React.useState(null);

  React.useEffect(() => {
    getJson("/api/v1/guest_portal/session").then((res) => {
      const isAuthed = Boolean(
        res.ok && res.json.data && res.json.data.authenticated,
      );
      setAuthed(isAuthed);
      if (!isAuthed) {
        setLoading(false);
        return;
      }
      getJson("/api/v1/guest_portal/bookings")
        .then((bookingsRes) => {
          if (bookingsRes.ok && bookingsRes.json.data)
            setBookings(bookingsRes.json.data.bookings || []);
          else setError(list.loadError);
        })
        .finally(() => setLoading(false));
    });
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  const activateThen = (bookingId, path) => {
    setActivatingId(bookingId);
    postJson(`/api/v1/guest_portal/bookings/${bookingId}/activate`).then(
      (res) => {
        if (res.ok) {
          router.visit(`/hebe/${buildingCode}/hgs/${path}`);
        } else {
          setError(list.loadError);
          setActivatingId(null);
        }
      },
    );
  };

  const goToDeposit = (bookingId) => activateThen(bookingId, "self-deposit");
  const goToDetail = (bookingId) => activateThen(bookingId, "booking-detail");

  const today = bookings.filter((booking) => booking.actionable);
  const refundGroup = bookings.filter(
    (booking) => !booking.actionable && booking.refund_outstanding,
  );
  const upcoming = bookings.filter(
    (booking) => !booking.actionable && !booking.refund_outstanding,
  );

  return (
    <Page>
      <PageHeader
        title={copy.title}
        backPath={`/hebe/${buildingCode}/hgs/`}
        useHomeIcon
        showGuestInfo
        buildingCode={buildingCode}
      />
      <Inner>
        <Lead>{copy.subtitle}</Lead>
        <Intro>{copy.intro}</Intro>

        {error ? <ErrorBox>{error}</ErrorBox> : null}

        {loading ? (
          <Centered>
            <Loader2 size={22} className="spin" />
          </Centered>
        ) : null}

        {!loading && authed === false ? (
          <GateCard>
            <GateIcon>
              <KeyRound size={22} />
            </GateIcon>
            <GateBody>
              <GateTitle>{list.notSignedInTitle}</GateTitle>
              <GateText>{list.notSignedInBody}</GateText>
              <GateActionLink href={`/hebe/${buildingCode}/hgs/self-deposit`}>
                {list.findBooking} <ArrowRight size={16} />
              </GateActionLink>
            </GateBody>
          </GateCard>
        ) : null}

        {!loading && authed ? (
          <>
            {bookings.length === 0 ? <EmptyNote>{list.empty}</EmptyNote> : null}

            {today.length > 0 ? (
              <Section>
                <SectionHeading>{list.todayHeading}</SectionHeading>
                {today.map((booking) => (
                  <BookingCard
                    key={booking.booking_id}
                    booking={booking}
                    list={list}
                    language={language}
                    busy={activatingId === booking.booking_id}
                    onOpen={() => goToDeposit(booking.booking_id)}
                    onDetail={() => goToDetail(booking.booking_id)}
                  />
                ))}
              </Section>
            ) : null}

            {upcoming.length > 0 ? (
              <Section>
                <SectionHeading>{list.upcomingHeading}</SectionHeading>
                {upcoming.map((booking) => (
                  <BookingCard
                    key={booking.booking_id}
                    booking={booking}
                    list={list}
                    language={language}
                    busy={activatingId === booking.booking_id}
                    onOpen={() => goToDeposit(booking.booking_id)}
                    onDetail={() => goToDetail(booking.booking_id)}
                  />
                ))}
              </Section>
            ) : null}

            {refundGroup.length > 0 ? (
              <Section>
                <SectionHeading>{list.refundHeading}</SectionHeading>
                {refundGroup.map((booking) => (
                  <BookingCard
                    key={booking.booking_id}
                    booking={booking}
                    list={list}
                    language={language}
                    busy={activatingId === booking.booking_id}
                    onOpen={() => goToDeposit(booking.booking_id)}
                    onDetail={() => goToDetail(booking.booking_id)}
                  />
                ))}
              </Section>
            ) : null}

            <ExitSessionButton buildingCode={buildingCode} />
          </>
        ) : null}

        <GateCard>
          <GateIcon>
            <RotateCcw size={22} />
          </GateIcon>
          <GateBody>
            <GateTitle>{copy.refund.title}</GateTitle>
            <GateText>{copy.refund.what}</GateText>
            <GateText>{copy.refund.why}</GateText>
            <StatusBadge>{copy.refund.status}</StatusBadge>
          </GateBody>
        </GateCard>
      </Inner>
    </Page>
  );
};

const BookingCard = (props) => {
  const booking = props.booking;
  const list = props.list;
  const payment = booking.deposit_payment || {};
  const paid = payment.paid;
  const expired = payment.expired;
  const isRefund = Boolean(booking.refund_outstanding && booking.checked_out);

  return (
    <Card>
      <CardTop>
        <GuestName>{booking.guest_name}</GuestName>
        {isRefund ? (
          <Badge $tone="refund">
            <RotateCcw size={14} /> {list.badgeRefund}
          </Badge>
        ) : paid ? (
          <Badge $tone="paid">
            <CheckCircle2 size={14} /> {list.badgePaid}
          </Badge>
        ) : expired ? (
          <Badge $tone="expired">{list.badgeExpired}</Badge>
        ) : (
          <Badge $tone="due">{list.badgeDue}</Badge>
        )}
      </CardTop>
      <Meta>
        {booking.nights} {list.nights} · {booking.rooms_count} {list.rooms}
        {booking.room_types && booking.room_types.length
          ? ` · ${booking.room_types.join(", ")}`
          : ""}
      </Meta>
      <Dates>
        <span>
          {list.checkIn}: {formatDate(booking.check_in, props.language)}
        </span>
        <span>
          {list.checkOut}: {formatDate(booking.check_out, props.language)}
        </span>
      </Dates>
      {!paid ? (
        <DepositRow>
          {rupiah(booking.deposit ? booking.deposit.total_idr : 0)}
        </DepositRow>
      ) : null}

      {paid ? (
        <>
          <PrimaryGold disabled={props.busy} onClick={props.onDetail}>
            {props.busy ? (
              <Loader2 size={16} className="spin" />
            ) : (
              list.seeBookingDetail
            )}
            {props.busy ? null : <ArrowRight size={16} />}
          </PrimaryGold>
          <CheckInHint>
            {isRefund ? list.refundHint : list.checkInInstructionHint}
          </CheckInHint>
        </>
      ) : booking.actionable ? (
        <Primary disabled={props.busy} onClick={props.onOpen}>
          {props.busy ? <Loader2 size={16} className="spin" /> : list.continue}
          {props.busy ? null : <ArrowRight size={16} />}
        </Primary>
      ) : (
        <MutedNote>{list.availableOnDay}</MutedNote>
      )}
    </Card>
  );
};

const Page = styled.div`
  background: #faf8f5;
  min-height: 100vh;
`;

const Inner = styled.div`
  margin: 0 auto;
  max-width: 768px;
  padding: 24px 16px 40px;
`;

const Lead = styled.p`
  color: #1a1a1a;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  margin: 0 0 8px;
`;

const Intro = styled.p`
  color: #4b5563;
  font-size: 14px;
  line-height: 1.6;
  margin: 0 0 22px;
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Section = styled.div`
  margin-bottom: 22px;
`;

const SectionHeading = styled.h2`
  color: #6b7280;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin: 0 0 10px;
  text-transform: uppercase;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  margin-bottom: 12px;
  padding: 16px 18px;
`;

const CardTop = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 12px;
  justify-content: space-between;
`;

const GuestName = styled.div`
  color: #1a1a1a;
  font-size: 16px;
  font-weight: 700;
`;

const Badge = styled.span`
  align-items: center;
  background: ${(p) =>
    p.$tone === "refund"
      ? "#e8f0fe"
      : p.$tone === "paid"
        ? "#e7f6ec"
        : p.$tone === "expired"
          ? "#fdecec"
          : "#fbf2dd"};
  border-radius: 999px;
  color: ${(p) =>
    p.$tone === "refund"
      ? "#1a56db"
      : p.$tone === "paid"
        ? "#1f7a44"
        : p.$tone === "expired"
          ? "#c0392b"
          : "#8a6d1a"};
  display: inline-flex;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  gap: 4px;
  padding: 5px 10px;
`;

const Meta = styled.div`
  color: #4b5563;
  font-size: 15px;
  line-height: 1.5;
  margin-top: 8px;
`;

const Dates = styled.div`
  color: #6b7280;
  display: flex;
  flex-wrap: wrap;
  font-size: 14px;
  gap: 4px 16px;
  line-height: 1.5;
  margin-top: 10px;
`;

const DepositRow = styled.div`
  color: #1a1a1a;
  font-size: 16px;
  font-weight: 700;
  margin-top: 10px;
`;

const MutedNote = styled.div`
  color: #9ca3af;
  font-size: 13px;
  font-style: italic;
  margin-top: 10px;
`;

const CheckInHint = styled.div`
  color: #6b7280;
  font-size: 12px;
  line-height: 1.5;
  margin-top: 8px;
  text-align: center;
`;

const PrimaryGold = styled.button`
  align-items: center;
  background: #c9a961;
  border: none;
  border-radius: 9px;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  font-size: 14px;
  font-weight: 600;
  gap: 6px;
  justify-content: center;
  margin-top: 14px;
  padding: 11px;
  width: 100%;

  &:disabled {
    opacity: 0.6;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Primary = styled.button`
  align-items: center;
  background: #c9a961;
  border: none;
  border-radius: 9px;
  color: #1a1a1a;
  cursor: pointer;
  display: flex;
  font-size: 14px;
  font-weight: 600;
  gap: 6px;
  justify-content: center;
  margin-top: 14px;
  padding: 11px;
  width: 100%;

  &:disabled {
    opacity: 0.6;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const EmptyNote = styled.p`
  color: #6b7280;
  font-size: 14px;
  margin: 0 0 20px;
`;

const ErrorBox = styled.div`
  background: #fdecec;
  border-radius: 8px;
  color: #c0392b;
  font-size: 13px;
  margin-bottom: 14px;
  padding: 10px 12px;
`;

const GateCard = styled.div`
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  display: flex;
  gap: 14px;
  margin-bottom: 14px;
  padding: 18px;
`;

const GateIcon = styled.div`
  align-items: center;
  background: rgba(201, 169, 97, 0.12);
  border-radius: 12px;
  color: #c9a961;
  display: flex;
  flex-shrink: 0;
  height: 46px;
  justify-content: center;
  width: 46px;
`;

const GateBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const GateTitle = styled.div`
  color: #1a1a1a;
  font-size: 17px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const GateText = styled.p`
  color: #4b5563;
  font-size: 14px;
  line-height: 1.55;
  margin: 0 0 8px;
`;

const GateActionLink = styled(Link)`
  align-items: center;
  background: #c9a961;
  border-radius: 9px;
  color: #1a1a1a;
  display: inline-flex;
  font-size: 14px;
  font-weight: 600;
  gap: 6px;
  margin-top: 6px;
  padding: 10px 16px;
  text-decoration: none;
  transition: background 0.2s;

  &:hover {
    background: rgba(201, 169, 97, 0.85);
  }
`;

const StatusBadge = styled.span`
  align-items: center;
  background: #f1f0ee;
  border-radius: 999px;
  color: #6b7280;
  display: inline-flex;
  font-size: 13px;
  font-weight: 600;
  margin-top: 4px;
  padding: 6px 12px;
`;

export default YourBookings;
